/**
 * Groups on screen: the circle drawn around each group's nodes (and its sub-groups), and the
 * forces that keep a group together and apart from its neighbours.
 *
 * Pure functions on plain data, so they are tested without a canvas (`groups.test.ts`). The
 * {@link Scene} calls them; nothing here knows about the view, the core or Solid.
 *
 * Coordinates are world coordinates. Radii include the padding, so a circle drawn with
 * `r * zoom` on screen surrounds what it contains. In the 3D view the same code works on
 * spheres: every point may have a depth `z` (missing means 0, so the 2D view is unchanged).
 */
import type { Group } from "@protocol/Group";

/** A circle in world coordinates (a sphere when it has a depth). */
export interface Circle {
  x: number;
  y: number;
  /** Depth, in the 3D view (missing: 0). */
  z?: number;
  r: number;
}

/** What a group needs from a node: where it is, how big it looks, and if it is shown. */
export interface GroupMember extends Circle {
  vx: number;
  vy: number;
  /** Speed along the depth, in the 3D view. */
  vz?: number;
  visible: boolean;
}

/** A group with its place on screen. */
export interface SimGroup<N extends GroupMember = GroupMember> extends Required<Circle> {
  id: string;
  group: Group;
  parent: SimGroup<N> | null;
  children: SimGroup<N>[];
  /** Nodes directly in this group. */
  members: N[];
  /** Nodes in this group or in any group inside it. */
  all: N[];
  /** 0 for a top-level group, 1 inside it, and so on. */
  depth: number;
  /** Shown when at least one of its nodes is. */
  visible: boolean;
}

/**
 * Builds the group tree from the graph's groups and the nodes' `group` field. A parent that
 * does not exist (or a cycle, which the core refuses anyway) makes a group top-level.
 */
export function buildGroups<N extends GroupMember>(groups: Group[], nodes: { sim: N; group: string | null }[]): SimGroup<N>[] {
  const byId = new Map<string, SimGroup<N>>();
  for (const group of groups) {
    byId.set(group.id, { id: group.id, group, parent: null, children: [], members: [], all: [], depth: 0, visible: false, x: 0, y: 0, z: 0, r: 0 });
  }
  for (const g of byId.values()) {
    const parent = g.group.parent ? byId.get(g.group.parent) : undefined;
    if (parent && !isInside(parent, g)) {
      g.parent = parent;
      parent.children.push(g);
    }
  }
  for (const g of byId.values()) g.depth = ancestors(g).length;
  for (const { sim, group } of nodes) {
    const g = group ? byId.get(group) : undefined;
    if (!g) continue;
    g.members.push(sim);
    for (let at: SimGroup<N> | null = g; at; at = at.parent) at.all.push(sim);
  }
  return [...byId.values()];
}

/** `true` when `g` is `inner` or one of its ancestors (attaching `inner` there would loop). */
function isInside<N extends GroupMember>(g: SimGroup<N>, inner: SimGroup<N>): boolean {
  for (let at: SimGroup<N> | null = g; at; at = at.parent) if (at === inner) return true;
  return false;
}

/**
 * How strongly a link between two nodes pulls, given their groups: fully inside one group,
 * half between groups that share a parent, weakly across unrelated groups. This lets groups
 * keep their shape while the links between them still hint at where they belong.
 */
export function linkPull(a: string | null, b: string | null, parents: Map<string, string | null>): number {
  if (a === b) return 1;
  if (a === null || b === null) return 0.35;
  const above = new Set<string>();
  for (let g: string | null | undefined = a; g; g = parents.get(g)) above.add(g);
  for (let g: string | null | undefined = b; g; g = parents.get(g)) if (above.has(g)) return 0.5;
  return 0.25;
}

/** The groups around `g`, nearest first. */
export function ancestors<N extends GroupMember>(g: SimGroup<N>): SimGroup<N>[] {
  const list: SimGroup<N>[] = [];
  for (let at = g.parent; at; at = at.parent) list.push(at);
  return list;
}

/**
 * A circle (or sphere) around every given one, plus `pad`. Not the smallest possible one, but
 * close and stable from one frame to the next: centred on the bounding box, wide enough for
 * the farthest edge. `null` for an empty list.
 */
export function enclose(circles: Circle[], pad: number): Required<Circle> | null {
  return encloseEach(circles, (c) => c.r, pad);
}

/**
 * {@link enclose} for `items` whose radius is `radius(item)`, without making a circle for each
 * (it runs for every group, three times per layout step: 40 000 nodes on a big project).
 */
function encloseEach<T extends { x: number; y: number; z?: number }>(items: readonly T[], radius: (item: T) => number, pad: number): Required<Circle> | null {
  let count = 0;
  let [x0, y0, z0, x1, y1, z1] = [Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity];
  for (const c of items) {
    const r = radius(c);
    if (r < 0) continue;
    const z = c.z ?? 0;
    count++;
    x0 = Math.min(x0, c.x - r);
    y0 = Math.min(y0, c.y - r);
    z0 = Math.min(z0, z - r);
    x1 = Math.max(x1, c.x + r);
    y1 = Math.max(y1, c.y + r);
    z1 = Math.max(z1, z + r);
  }
  if (!count) return null;
  const [x, y, z] = [(x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2];
  let far = 0;
  for (const c of items) {
    const r = radius(c);
    if (r < 0) continue;
    const dx = c.x - x;
    const dy = c.y - y;
    const dz = (c.z ?? 0) - z;
    far = Math.max(far, Math.sqrt(dx * dx + dy * dy + dz * dz) + r);
  }
  return { x, y, z, r: far + pad };
}

/**
 * Places every group's circle around its visible nodes and sub-groups, innermost first, so a
 * parent's circle surrounds its children's circles. `pad` is the room between a circle and
 * what it contains, and `radius` how big a node looks (both in world units).
 */
export function layoutGroups<N extends GroupMember>(groups: SimGroup<N>[], pad: number, radius: (n: N) => number = (n) => n.r): void {
  const deepestFirst = [...groups].sort((a, b) => b.depth - a.depth);
  // A member or sub-group that is hidden has a radius of -1: left out.
  const inside: (N | SimGroup<N>)[] = [];
  const size = (c: N | SimGroup<N>) => (!c.visible ? -1 : "members" in c ? c.r : radius(c));
  for (const g of deepestFirst) {
    inside.length = 0;
    for (const n of g.members) inside.push(n);
    for (const child of g.children) inside.push(child);
    const circle = encloseEach(inside, size, pad);
    g.visible = !!circle;
    if (circle) Object.assign(g, circle);
  }
}

/**
 * Pulls each node towards the centre of its group, and more gently towards the groups around
 * it, so groups stay compact. `alpha` is the simulation's energy.
 */
export function pullTogether<N extends GroupMember>(groups: SimGroup<N>[], alpha: number): void {
  for (const g of groups) {
    if (!g.visible) continue;
    const strength = 0.05;
    for (const n of g.members) {
      if (!n.visible) continue;
      let k = strength;
      for (let at: SimGroup<N> | null = g; at; at = at.parent) {
        if (at.visible) {
          n.vx += (at.x - n.x) * k * alpha;
          n.vy += (at.y - n.y) * k * alpha;
          if (n.vz !== undefined) n.vz += ((at.z ?? 0) - (n.z ?? 0)) * k * alpha;
        }
        k *= 0.4;
      }
    }
  }
}

/** Something that takes room at one level of the tree: a group, or a node outside groups. */
interface Item extends Required<Circle> {
  /** The nodes to move when this item is pushed. */
  nodes: (GroupMember & { fixed?: boolean })[];
  isGroup: boolean;
}

/**
 * Moves overlapping neighbours apart: at every level (the top, then inside each group), each
 * group is pushed away from the other groups and nodes beside it until there is `gap` between
 * them. Nodes among themselves are already kept apart by the scene's own repulsion.
 *
 * This moves positions directly (like collision handling) rather than adding speed, so links
 * between groups cannot pull them back over each other. Each step removes `strength` of the
 * overlap; the bigger side moves less. Nodes held by the pointer (`fixed`) do not move.
 */
export function pushApart<N extends GroupMember & { fixed?: boolean }>(groups: SimGroup<N>[], loose: N[], gap: number, strength = 0.3): void {
  const asItem = (g: SimGroup<N>): Item => ({ x: g.x, y: g.y, z: g.z ?? 0, r: g.r, nodes: g.all.filter((n) => n.visible), isGroup: true });
  const asNode = (n: N): Item => ({ x: n.x, y: n.y, z: n.z ?? 0, r: n.r, nodes: [n], isGroup: false });
  const levels: Item[][] = [[...groups.filter((g) => !g.parent && g.visible).map(asItem), ...loose.filter((n) => n.visible).map(asNode)]];
  for (const g of groups) {
    if (!g.visible || !g.children.some((c) => c.visible)) continue;
    levels.push([...g.children.filter((c) => c.visible).map(asItem), ...g.members.filter((n) => n.visible).map(asNode)]);
  }
  for (const items of levels) {
    for (let i = 0; i < items.length; i++) {
      for (let j = i + 1; j < items.length; j++) {
        const a = items[i]!;
        const b = items[j]!;
        if (!a.isGroup && !b.isGroup) continue;
        let dx = b.x - a.x;
        let dy = b.y - a.y;
        const dz = b.z - a.z;
        let d = Math.hypot(dx, dy, dz);
        if (d < 0.01) {
          dx = 1;
          dy = 0;
          d = 1;
        }
        const overlap = a.r + b.r + gap - d;
        if (overlap <= 0) continue;
        const shift = Math.min(overlap * strength, MAX_SHIFT);
        const share = b.nodes.length / (a.nodes.length + b.nodes.length || 1);
        const u = [dx / d, dy / d, dz / d] as const;
        shiftAll(a, u.map((c) => -c * shift * share) as Shift);
        shiftAll(b, u.map((c) => c * shift * (1 - share)) as Shift);
      }
    }
  }
}

/** The most an item moves in one step, so a big overlap is resolved over several steps. */
const MAX_SHIFT = 6;

/** A move along x, y and z. */
type Shift = [number, number, number];

/** Moves an item's nodes (and the item itself, so later pairs see where it went). Depth moves only for nodes that have one. */
function shiftAll(item: Item, [dx, dy, dz]: Shift): void {
  for (const n of item.nodes) {
    if (n.fixed) continue;
    n.x += dx;
    n.y += dy;
    if (n.z !== undefined) n.z += dz;
  }
  item.x += dx;
  item.y += dy;
  item.z += dz;
}
